import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchUnreadNotificationCount } from '../lib/supabase';

function useIsMobile() {
  const [ok, setOk] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 1024 : false
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    const fn = (e) => setOk(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return ok;
}

export default function MobileNav() {
  const isMobile  = useIsMobile();
  const location  = useLocation();
  const navigate  = useNavigate();
  const { user, isPro, isAdmin } = useAuth();
  const [notifCount, setNotifCount] = useState(0);

  useEffect(() => {
    if (!user) return;
    const load = () => fetchUnreadNotificationCount(user.id).then(setNotifCount).catch(() => {});
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [user?.id]);

  if (!isMobile) return null;

  const path = location.pathname;
  const blocked = ['/', '/auth', '/join', '/auth/callback', '/reset-password',
                   '/setup/username', '/team/create', '/team/new'];
  if (blocked.includes(path)) return null;
  if (path.startsWith('/legal/') || path.startsWith('/entry/')) return null;

  const search        = location.search;
  const isHome        = path === '/app' && !search.includes('filter=mine') && !search.includes('newEntry=');
  const isLibrary     = path === '/app' && search.includes('filter=mine');
  const isSkills      = path.startsWith('/skills');
  const isMarketplace = path.startsWith('/marketplace');
  const isCreator     = path === '/creator';
  const isAccount     = path === '/account' || path === '/settings' || path === '/admin';

  const go = (dest) => navigate(dest);

  const TABS = [
    // ── Left of FAB: Home · Library · Skills ──
    {
      id: 'home', label: 'Home', active: isHome,
      onClick: () => go('/app'),
      icon: (on) => (
        <svg width="22" height="22" viewBox="0 0 24 24"
          fill={on ? 'var(--accent)' : 'none'} stroke={on ? 'var(--accent)' : 'currentColor'}
          strokeWidth={on ? 2 : 1.6} strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
          <path d="M9 21V12h6v9" fill="none"/>
        </svg>
      ),
    },
    {
      id: 'library', label: 'Library', active: isLibrary,
      onClick: () => { if (!user) { go('/auth'); return; } go('/app?filter=mine'); },
      icon: (on) => (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
          stroke={on ? 'var(--accent)' : 'currentColor'}
          strokeWidth={on ? 2 : 1.6} strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" rx="1.5" fill={on ? 'var(--accent)' : 'none'}/>
          <rect x="14" y="3" width="7" height="7" rx="1.5" fill={on ? 'var(--accent)' : 'none'}/>
          <rect x="14" y="14" width="7" height="7" rx="1.5" fill={on ? 'var(--accent)' : 'none'} opacity={on ? 0.55 : 1}/>
          <rect x="3" y="14" width="7" height="7" rx="1.5" fill={on ? 'var(--accent)' : 'none'} opacity={on ? 0.55 : 1}/>
        </svg>
      ),
    },
    {
      id: 'skills', label: 'Skills', active: isSkills,
      onClick: () => {
        if (!user) { go('/auth'); return; }
        go('/skills');
      },
      icon: (on) => (
        <svg width="22" height="22" viewBox="0 0 24 24"
          fill={on ? 'var(--accent)' : 'none'} stroke={on ? 'var(--accent)' : 'currentColor'}
          strokeWidth={on ? 2 : 1.6} strokeLinecap="round" strokeLinejoin="round">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
        </svg>
      ),
    },
    // FAB slot
    { id: 'fab-gap', isFabSlot: true },
    // ── Right of FAB: Market · Creator · Account ──
    {
      id: 'market', label: 'Market', active: isMarketplace,
      onClick: () => go('/marketplace'),
      icon: (on) => (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
          stroke={on ? 'var(--accent)' : 'currentColor'}
          strokeWidth={on ? 2 : 1.6} strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill={on ? 'var(--accent)' : 'none'}/>
          <polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
      ),
    },
    {
      id: 'creator', label: 'Creator', active: isCreator,
      onClick: () => go('/creator'), badge: notifCount > 0 ? notifCount : 0,
      icon: (on) => (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
          stroke={on ? 'var(--accent)' : 'currentColor'}
          strokeWidth={on ? 2 : 1.6} strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
            fill={on ? 'var(--accent)' : 'none'}/>
        </svg>
      ),
    },
    {
      id: 'account', label: 'Account', active: isAccount,
      onClick: () => { if (!user) { go('/auth'); return; } go('/account'); },
      icon: (on) => (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
          stroke={on ? 'var(--accent)' : 'currentColor'}
          strokeWidth={on ? 2 : 1.6} strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="4" fill={on ? 'var(--accent)' : 'none'}/>
          <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
        </svg>
      ),
    },
  ];

  const FAB_GAP    = 72;  // px — transparent slot for the FAB
  const NAV_BOTTOM = 'calc(12px + env(safe-area-inset-bottom, 0px))';

  const tabBtn = (tab) => (
    <button
      key={tab.id}
      onClick={tab.onClick}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        height: '100%',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: tab.active ? 'var(--accent)' : 'var(--dim)',
        padding: '0 2px',
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
        transition: 'color 0.15s',
      }}
    >
      {tab.icon(tab.active)}
      <span style={{ position: 'relative', fontSize: 9.5, fontWeight: tab.active ? 700 : 400, letterSpacing: '0.02em', lineHeight: 1 }}>
        {tab.label}
        {tab.badge > 0 && (
          <span style={{
            position: 'absolute', top: -5, right: -9,
            background: '#e24b4a', color: '#fff',
            fontSize: 8, fontWeight: 700,
            minWidth: 13, height: 13, borderRadius: 7,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 2px', border: '1.5px solid var(--bg)',
          }}>{tab.badge > 9 ? '9+' : tab.badge}</span>
        )}
      </span>
    </button>
  );

  // 6 real tabs: Home, Library, Skills | [FAB] | Market, Creator, Account
  // slice(0,3) = left pill, slice(3) = right pill
  const leftTabs  = TABS.filter(t => !t.isFabSlot).slice(0, 3);
  const rightTabs = TABS.filter(t => !t.isFabSlot).slice(3);

  // Shared pill style — overflow hidden clips anything that bleeds toward gap
  const pill = {
    position: 'fixed',
    bottom: NAV_BOTTOM,
    height: 62,
    background: 'var(--surface)',
    border: '0.5px solid var(--border2)',
    // Tight shadow — stays under each pill, doesn't reach the gap
    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
    display: 'flex',
    alignItems: 'center',
    zIndex: 200,
    fontFamily: 'var(--font)',
    overflow: 'hidden',  // clips any content/effects at the inner edge
  };

  return (
    <>
      {/* Left pill */}
      <nav style={{
        ...pill,
        left: 16,
        right: `calc(50% + ${FAB_GAP / 2}px)`,
        borderRadius: '20px 12px 12px 20px',
      }}>
        {leftTabs.map(tabBtn)}
      </nav>

      {/* Right pill */}
      <nav style={{
        ...pill,
        left: `calc(50% + ${FAB_GAP / 2}px)`,
        right: 16,
        borderRadius: '12px 20px 20px 12px',
      }}>
        {rightTabs.map(tabBtn)}
      </nav>
    </>
  );
}
